import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useMemo } from 'react';
import { useAuthStore } from '../features/auth/stores/useAuthStore';
import { ensureGuestSession } from '../providers/GuestProvider';

export interface WishlistItem {
    id: string;
    productId: string;
    variantId: string | null;
    product: {
        id: string;
        name: string;
        slug?: string;
        price: number;
        compareAtPrice?: number | null;
        images?: { url: string }[];
        translations?: any[];
    };
    variant?: {
        id: string;
        name: string;
        price: number | null;
    } | null;
    createdAt: string;
}

// 1. Fetch Wishlist
export const useWishlist = () => {
    return useQuery<WishlistItem[]>({
        queryKey: ['wishlist'],
        queryFn: async () => {
            const { data } = await api.get('/wishlists');
            return data.data || [];
        },
        staleTime: 1000 * 60 * 5,
    });
};

// 2. Convenience: Set of wishlisted product IDs for O(1) lookup
export const useWishlistIds = () => {
    const { data: items } = useWishlist();
    return useMemo(() => {
        const ids = new Set<string>();
        if (items) {
            items.forEach(item => ids.add(item.productId));
        }
        return ids;
    }, [items]);
};

// 3. Check if a specific product is wishlisted
export const useIsWishlisted = (productId: string) => {
    const ids = useWishlistIds();
    return ids.has(productId);
};

// 4. Toggle Wishlist Item (Optimistic UI)
export const useToggleWishlist = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: { productId: string; variantId?: string | null }) => {
            const { isAuthenticated, guestId } = useAuthStore.getState();
            if (!isAuthenticated && !guestId) {
                await ensureGuestSession();
            }
            const { data } = await api.post('/wishlists/toggle', payload);
            return data; // { success, added, message, item? }
        },
        // Optimistic update: toggle the item in the cache immediately
        onMutate: async (payload) => {
            await queryClient.cancelQueries({ queryKey: ['wishlist'] });
            const previousItems = queryClient.getQueryData<WishlistItem[]>(['wishlist']);

            queryClient.setQueryData<WishlistItem[]>(['wishlist'], (old = []) => {
                const exists = old.some(item => item.productId === payload.productId);
                if (exists) {
                    // Remove it optimistically
                    return old.filter(item => item.productId !== payload.productId);
                } else {
                    // Add a placeholder optimistically
                    return [
                        {
                            id: `temp-${Date.now()}`,
                            productId: payload.productId,
                            variantId: payload.variantId || null,
                            product: { id: payload.productId, name: '', price: 0 },
                            createdAt: new Date().toISOString(),
                        },
                        ...old
                    ];
                }
            });

            return { previousItems };
        },
        onSuccess: (data) => {
            if (data?.guestId && !useAuthStore.getState().isAuthenticated) {
                useAuthStore.getState().setGuestId(data.guestId);
            }
        },
        onError: (_err, _payload, context) => {
            // Rollback on error
            if (context?.previousItems) {
                queryClient.setQueryData(['wishlist'], context.previousItems);
            }
        },
        onSettled: () => {
            // Refetch to sync with the server
            queryClient.invalidateQueries({ queryKey: ['wishlist'] });
        },
    });
};

// 5. Clear Wishlist
export const useClearWishlist = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            const { data } = await api.delete('/wishlists/clear');
            return data;
        },
        onSuccess: () => {
            queryClient.setQueryData(['wishlist'], []);
            queryClient.invalidateQueries({ queryKey: ['wishlist'] });
        },
    });
};
